import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import {
    Package,
    Plus,
    Edit,
    Trash2,
    Search,
    X,
    Save,
    Link as LinkIcon,
    Shield,
    Image,
    Mail,
    ExternalLink
} from 'lucide-react';

interface Product {
    id: string;
    name: string;
    description?: string;
    commission_type: 'percentage' | 'fixed';
    default_commission: number;
    currency: string;
    landing_page_url: string;
    active: boolean;
    country_overrides?: Record<string, { type: string; value: number }>;
    whitelisted_ips?: string[];
    events?: Record<string, { type: 'percentage' | 'fixed'; value: number }>;
    targeting_rules?: {
        allowed_countries?: string[];
        allowed_devices?: string[];
        allowed_os?: string[];
        fallback_url?: string;
    };
    landing_pages?: { id: string; name: string; url: string }[];
    creatives?: {
        landing_pages?: { id: string; name: string; url: string }[];
        banners?: { id: string; name: string; url: string; dimensions?: string }[];
        email_templates?: { id: string; name: string; subject: string; preview_url?: string }[];
    };
}

// Available Standard Events
const STANDARD_EVENTS = [
    'purchase', 'add_to_cart', 'click_link', 'view_content',
    'select_payment_method', 'payment_failed', 'upsell',
    'finished_quiz', 'started_quiz', 'clicked_lp'
];

// Creatives Editor Component
function CreativesEditor({ product, onClose, onSave }: { product: Product; onClose: () => void; onSave: () => void }) {
    const [creatives, setCreatives] = useState(product.creatives || {
        landing_pages: [],
        banners: [],
        email_templates: []
    });
    const [activeTab, setActiveTab] = useState<'landing_pages' | 'banners' | 'email_templates'>('landing_pages');
    const [saving, setSaving] = useState(false);

    const addLandingPage = () => {
        setCreatives({
            ...creatives,
            landing_pages: [...(creatives.landing_pages || []), { id: Date.now().toString(), name: '', url: '' }]
        });
    };

    const addBanner = () => {
        setCreatives({
            ...creatives,
            banners: [...(creatives.banners || []), { id: Date.now().toString(), name: '', url: '', dimensions: '' }]
        });
    };

    const addEmailTemplate = () => {
        setCreatives({
            ...creatives,
            email_templates: [...(creatives.email_templates || []), { id: Date.now().toString(), name: '', subject: '', preview_url: '' }]
        });
    };

    const updateLandingPage = (id: string, field: string, value: string) => {
        setCreatives({
            ...creatives,
            landing_pages: creatives.landing_pages?.map(lp => lp.id === id ? { ...lp, [field]: value } : lp) || []
        });
    };

    const updateBanner = (id: string, field: string, value: string) => {
        setCreatives({
            ...creatives,
            banners: creatives.banners?.map(b => b.id === id ? { ...b, [field]: value } : b) || []
        });
    };

    const updateEmailTemplate = (id: string, field: string, value: string) => {
        setCreatives({
            ...creatives,
            email_templates: creatives.email_templates?.map(et => et.id === id ? { ...et, [field]: value } : et) || []
        });
    };

    const removeLandingPage = (id: string) => {
        setCreatives({ ...creatives, landing_pages: creatives.landing_pages?.filter(lp => lp.id !== id) || [] });
    };

    const removeBanner = (id: string) => {
        setCreatives({ ...creatives, banners: creatives.banners?.filter(b => b.id !== id) || [] });
    };

    const removeEmailTemplate = (id: string) => {
        setCreatives({ ...creatives, email_templates: creatives.email_templates?.filter(et => et.id !== id) || [] });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateDoc(doc(db, 'products', product.id), { creatives, updated_at: Timestamp.now() });
            onSave();
        } catch (error) {
            console.error('Error saving creatives:', error);
            alert('Failed to save creatives');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-dark rounded-2xl p-6 w-full max-w-4xl animate-slide-up max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-white">Manage Creatives - {product.name}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={24} /></button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6 border-b border-white/10">
                    <button onClick={() => setActiveTab('landing_pages')} className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${activeTab === 'landing_pages' ? 'text-primary-400 border-b-2 border-primary-400' : 'text-gray-400 hover:text-white'}`}>
                        <ExternalLink size={16} /> Entry Links ({creatives.landing_pages?.length || 0})
                    </button>
                    <button onClick={() => setActiveTab('banners')} className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${activeTab === 'banners' ? 'text-primary-400 border-b-2 border-primary-400' : 'text-gray-400 hover:text-white'}`}>
                        <Image size={16} /> Banners ({creatives.banners?.length || 0})
                    </button>
                    <button onClick={() => setActiveTab('email_templates')} className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${activeTab === 'email_templates' ? 'text-primary-400 border-b-2 border-primary-400' : 'text-gray-400 hover:text-white'}`}>
                        <Mail size={16} /> Email Templates ({creatives.email_templates?.length || 0})
                    </button>
                </div>

                {/* Landing Pages Tab */}
                {activeTab === 'landing_pages' && (
                    <div className="space-y-4">
                        {creatives.landing_pages?.map(lp => (
                            <div key={lp.id} className="flex gap-2 items-start p-4 bg-white/5 rounded-lg">
                                <div className="flex-1 space-y-2">
                                    <input type="text" placeholder="Link name (e.g., Advertorial, Advertorial Male)" value={lp.name} onChange={e => updateLandingPage(lp.id, 'name', e.target.value)} className="input-field" />
                                    <input type="url" placeholder="https://example.com/post/ (no language prefix — added automatically)" value={lp.url} onChange={e => updateLandingPage(lp.id, 'url', e.target.value)} className="input-field" />
                                </div>
                                <button onClick={() => removeLandingPage(lp.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 size={18} /></button>
                            </div>
                        ))}
                        <button onClick={addLandingPage} className="btn-secondary w-full flex items-center justify-center gap-2"><Plus size={18} /> Add Entry Link</button>
                    </div>
                )}

                {/* Banners Tab */}
                {activeTab === 'banners' && (
                    <div className="space-y-4">
                        {creatives.banners?.map(banner => (
                            <div key={banner.id} className="flex gap-2 items-start p-4 bg-white/5 rounded-lg">
                                <div className="flex-1 space-y-2">
                                    <input type="text" placeholder="Banner name (e.g., Homepage 728x90)" value={banner.name} onChange={e => updateBanner(banner.id, 'name', e.target.value)} className="input-field" />
                                    <input type="url" placeholder="https://example.com/banner.jpg" value={banner.url} onChange={e => updateBanner(banner.id, 'url', e.target.value)} className="input-field" />
                                    <input type="text" placeholder="Dimensions (e.g., 728x90)" value={banner.dimensions || ''} onChange={e => updateBanner(banner.id, 'dimensions', e.target.value)} className="input-field" />
                                </div>
                                <button onClick={() => removeBanner(banner.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 size={18} /></button>
                            </div>
                        ))}
                        <button onClick={addBanner} className="btn-secondary w-full flex items-center justify-center gap-2"><Plus size={18} /> Add Banner</button>
                    </div>
                )}

                {/* Email Templates Tab */}
                {activeTab === 'email_templates' && (
                    <div className="space-y-4">
                        {creatives.email_templates?.map(template => (
                            <div key={template.id} className="flex gap-2 items-start p-4 bg-white/5 rounded-lg">
                                <div className="flex-1 space-y-2">
                                    <input type="text" placeholder="Template name (e.g., Promo Email)" value={template.name} onChange={e => updateEmailTemplate(template.id, 'name', e.target.value)} className="input-field" />
                                    <input type="text" placeholder="Email subject line" value={template.subject} onChange={e => updateEmailTemplate(template.id, 'subject', e.target.value)} className="input-field" />
                                    <input type="url" placeholder="Preview URL (optional)" value={template.preview_url || ''} onChange={e => updateEmailTemplate(template.id, 'preview_url', e.target.value)} className="input-field" />
                                </div>
                                <button onClick={() => removeEmailTemplate(template.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 size={18} /></button>
                            </div>
                        ))}
                        <button onClick={addEmailTemplate} className="btn-secondary w-full flex items-center justify-center gap-2"><Plus size={18} /> Add Email Template</button>
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-6 mt-6 border-t border-white/10">
                    <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
                        {saving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Save size={18} /> Save Creatives</>}
                    </button>
                </div>
            </div>
        </div>
    );
}


export default function AdminProducts() {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showCreativesModal, setShowCreativesModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [creativesProduct, setCreativesProduct] = useState<Product | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        currency: 'EUR',
        landing_page_url: '',
        active: true,
        whitelisted_ips: '',
        events: {} as Record<string, { type: 'percentage' | 'fixed'; value: number }>,
        targeting_countries: '',
        targeting_devices: '',
        targeting_os: '',
        targeting_fallback: '',
        landing_pages: [] as { id: string; name: string; url: string }[]
    });

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const snapshot = await getDocs(collection(db, 'products'));
            const productsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Product[];
            setProducts(productsList);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setEditingProduct(null);
        setFormData({
            name: '',
            description: '',
            currency: 'EUR',
            landing_page_url: '',
            active: true,
            whitelisted_ips: '',
            events: {},
            targeting_countries: '',
            targeting_devices: '',
            targeting_os: '',
            targeting_fallback: '',
            landing_pages: []
        });
        setShowModal(true);
    };

    const openEditModal = (product: Product) => {
        setEditingProduct(product);

        // Auto-migrate: if product has default_commission but no events, create a purchase event
        let events = product.events || {};
        if (product.default_commission && Object.keys(events).length === 0) {
            events = {
                purchase: {
                    type: product.commission_type || 'percentage',
                    value: product.default_commission
                }
            };
        }

        setFormData({
            name: product.name,
            description: product.description || '',
            currency: product.currency,
            landing_page_url: product.landing_page_url,
            active: product.active,
            whitelisted_ips: product.whitelisted_ips?.join(', ') || '',
            events,
            targeting_countries: product.targeting_rules?.allowed_countries?.join(', ') || '',
            targeting_devices: product.targeting_rules?.allowed_devices?.join(', ') || '',
            targeting_os: product.targeting_rules?.allowed_os?.join(', ') || '',
            targeting_fallback: product.targeting_rules?.fallback_url || '',
            landing_pages: product.landing_pages || []
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!formData.name || !formData.landing_page_url) {
            alert('Please fill in all required fields');
            return;
        }

        if (Object.keys(formData.events).length === 0) {
            alert('Please configure at least one event payout');
            return;
        }

        setActionLoading(true);
        try {
            const whitelistedIps = formData.whitelisted_ips
                .split(',')
                .map(ip => ip.trim())
                .filter(ip => ip.length > 0);

            // Compute default_commission from events for backward compatibility
            const primaryEvent = formData.events['purchase'] || Object.values(formData.events)[0];
            const productData = {
                name: formData.name,
                description: formData.description,
                commission_type: primaryEvent.type,
                default_commission: primaryEvent.value,
                currency: formData.currency,
                landing_page_url: formData.landing_page_url,
                active: formData.active,
                whitelisted_ips: whitelistedIps,
                events: formData.events,
                landing_pages: formData.landing_pages.filter(lp => lp.name.trim() !== '' && lp.url.trim() !== ''),
                targeting_rules: {
                    allowed_countries: formData.targeting_countries.split(',').map(c => c.trim().toUpperCase()).filter(c => c.length === 2),
                    allowed_devices: formData.targeting_devices.split(',').map(d => d.trim().toLowerCase()).filter(d => d.length > 0),
                    allowed_os: formData.targeting_os.split(',').map(o => o.trim()).filter(o => o.length > 0),
                    fallback_url: formData.targeting_fallback
                },
                updated_at: Timestamp.now(),
            };

            if (editingProduct) {
                await updateDoc(doc(db, 'products', editingProduct.id), productData);
            } else {
                await addDoc(collection(db, 'products'), {
                    ...productData,
                    created_at: Timestamp.now(),
                });
            }

            setShowModal(false);
            fetchProducts();
        } catch (error) {
            console.error('Error saving product:', error);
            alert('Failed to save product');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (productId: string, productName: string) => {
        if (!confirm(`Are you sure you want to delete "${productName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            await deleteDoc(doc(db, 'products', productId));
            fetchProducts();
        } catch (error) {
            console.error('Error deleting product:', error);
            alert('Failed to delete product');
        }
    };

    const toggleActive = async (product: Product) => {
        try {
            await updateDoc(doc(db, 'products', product.id), {
                active: !product.active,
                updated_at: Timestamp.now(),
            });
            fetchProducts();
        } catch (error) {
            console.error('Error toggling product status:', error);
        }
    };

    const filteredProducts = products.filter(product => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return product.name.toLowerCase().includes(term);
    });

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Product Management</h1>
                    <p className="text-gray-400">Manage products and commission rates</p>
                </div>
                <button onClick={openCreateModal} className="btn-primary flex items-center gap-2">
                    <Plus size={20} />
                    Add Product
                </button>
            </div>

            {/* Search */}
            <div className="card">
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input-field pl-12"
                    />
                </div>
            </div>

            {/* Products Grid */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : filteredProducts.length === 0 ? (
                <div className="card text-center py-12">
                    <Package className="mx-auto text-gray-500 mb-4" size={48} />
                    <h3 className="text-lg font-semibold text-white mb-2">No Products Found</h3>
                    <p className="text-gray-400 mb-4">Create your first product to get started.</p>
                    <button onClick={openCreateModal} className="btn-primary">
                        <Plus size={20} className="mr-2" />
                        Add Product
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProducts.map((product) => (
                        <div key={product.id} className="card">
                            <div className="flex items-start justify-between mb-4">
                                <div className="w-12 h-12 rounded-xl bg-linear-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                                    <Package className="text-white" size={24} />
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => toggleActive(product)}
                                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${product.active
                                            ? 'bg-emerald-500/20 text-emerald-400'
                                            : 'bg-gray-500/20 text-gray-400'
                                            }`}
                                    >
                                        {product.active ? 'Active' : 'Inactive'}
                                    </button>
                                </div>
                            </div>

                            <h3 className="text-lg font-semibold text-white mb-1">{product.name}</h3>
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-[10px] font-mono bg-white/5 text-gray-400 px-2 py-0.5 rounded border border-white/10 select-all" title="Copy Product ID">
                                    ID: {product.id}
                                </span>
                            </div>
                            <p className="text-sm text-gray-400 mb-4 line-clamp-2">{product.description || 'No description'}</p>

                            <div className="space-y-2 mb-4">
                                <div className="flex items-center gap-2 text-sm">
                                    <LinkIcon size={14} className="text-gray-400" />
                                    <span className="text-gray-400 truncate">{product.landing_page_url}</span>
                                </div>
                                {product.whitelisted_ips && product.whitelisted_ips.length > 0 && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Shield size={14} className="text-amber-400" />
                                        <span className="text-amber-400">{product.whitelisted_ips.length} IP(s) whitelisted</span>
                                    </div>
                                )}
                                {product.targeting_rules && (Object.keys(product.targeting_rules.allowed_countries || {}).length > 0) && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-purple-400 text-xs">Targeting Enabled ({product.targeting_rules?.allowed_countries?.length || 0} Countries)</span>
                                    </div>
                                )}
                            </div>

                            {/* Event Payouts */}
                            <div className="mb-4">
                                <p className="text-xs text-gray-500 mb-2">Event Payouts:</p>
                                <div className="flex flex-wrap gap-1">
                                    {Object.keys(product.events || {}).length > 0 ? (
                                        Object.entries(product.events || {}).map(([evt, rate]) => (
                                            <span key={evt} className="px-2 py-1 bg-emerald-500/20 rounded text-xs text-emerald-300">
                                                {evt}: {rate.value}{rate.type === 'percentage' ? '%' : ' ' + product.currency}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="px-2 py-1 bg-emerald-500/20 rounded text-xs text-emerald-300">
                                            default: {product.default_commission}{product.commission_type === 'percentage' ? '%' : ' ' + product.currency}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Country Overrides */}
                            {Object.keys(product.country_overrides || {}).length > 0 && (
                                <div className="mb-4">
                                    <p className="text-xs text-gray-500 mb-2">Country Overrides:</p>
                                    <div className="flex flex-wrap gap-1">
                                        {Object.entries(product.country_overrides || {}).map(([country, rate]) => (
                                            <span key={country} className="px-2 py-1 bg-blue-500/20 rounded text-xs text-blue-300">
                                                {country}: {typeof rate === 'number' ? `${rate}%` : `${rate.value}${rate.type === 'percentage' ? '%' : ' ' + product.currency}`}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center gap-2 pt-4 border-t border-white/10">
                                <button
                                    onClick={() => openEditModal(product)}
                                    className="btn-secondary py-2 px-3 text-sm flex-1 flex items-center justify-center gap-2"
                                >
                                    <Edit size={16} />
                                    Edit
                                </button>
                                <button
                                    onClick={() => {
                                        setCreativesProduct(product);
                                        setShowCreativesModal(true);
                                    }}
                                    className="btn-secondary py-2 px-3 text-sm flex items-center justify-center gap-2"
                                    title="Manage Creatives"
                                >
                                    <Image size={16} />
                                </button>
                                <button
                                    onClick={() => handleDelete(product.id, product.name)}
                                    className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="glass-dark rounded-2xl p-6 w-full max-w-lg animate-slide-up max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-white">
                                {editingProduct ? 'Edit Product' : 'Create Product'}
                            </h2>
                            <button
                                onClick={() => setShowModal(false)}
                                className="text-gray-400 hover:text-white"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Product Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g., Pro Plan"
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Brief product description..."
                                    rows={3}
                                    className="input-field resize-none"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Currency
                                    </label>
                                    <select
                                        value={formData.currency}
                                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                        className="input-field"
                                    >
                                        <option value="EUR">EUR</option>
                                        <option value="USD">USD</option>
                                        <option value="GBP">GBP</option>
                                    </select>
                                </div>
                            </div>

                            <hr className="border-white/10" />

                            <h3 className="text-lg font-semibold text-white">Event Payouts *</h3>
                            <p className="text-xs text-gray-500 -mt-3">Configure commission for each action. At least one event is required.</p>
                            <div className="space-y-4">
                                {Object.entries(formData.events).map(([evt, config]) => (
                                    <div key={evt} className="flex gap-2 items-center bg-white/5 p-3 rounded-lg">
                                        <span className="flex-1 text-sm text-gray-300">{evt}</span>
                                        <select
                                            value={config.type}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                events: { ...prev.events, [evt]: { ...prev.events[evt], type: e.target.value as 'percentage' | 'fixed' } }
                                            }))}
                                            className="input-field w-32 py-1 text-sm bg-gray-800"
                                        >
                                            <option value="percentage">Percent (%)</option>
                                            <option value="fixed">Fixed ({formData.currency})</option>
                                        </select>
                                        <input
                                            type="number"
                                            value={config.value}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                events: { ...prev.events, [evt]: { ...prev.events[evt], value: parseFloat(e.target.value) } }
                                            }))}
                                            className="input-field w-24 py-1 text-sm bg-gray-800"
                                            placeholder="Value"
                                        />
                                        <button
                                            onClick={() => {
                                                const newEvents = { ...formData.events };
                                                delete newEvents[evt];
                                                setFormData({ ...formData, events: newEvents });
                                            }}
                                            className="text-red-400 hover:text-red-300 p-2"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}

                                <div className="flex gap-2">
                                    <select
                                        id="add-event-select-top"
                                        className="input-field flex-1"
                                        defaultValue=""
                                        onChange={(e) => {
                                            const eventName = e.target.value;
                                            if (eventName && !formData.events[eventName]) {
                                                setFormData({
                                                    ...formData,
                                                    events: {
                                                        ...formData.events,
                                                        [eventName]: { type: 'fixed', value: 0 }
                                                    }
                                                });
                                            }
                                            e.target.value = "";
                                        }}
                                    >
                                        <option value="" disabled>Add Event Payout...</option>
                                        {STANDARD_EVENTS.filter(e => !formData.events[e]).map(evt => (
                                            <option key={evt} value={evt}>{evt}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <hr className="border-white/10" />

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Landing Page URL *
                                </label>
                                <input
                                    type="url"
                                    value={formData.landing_page_url}
                                    onChange={(e) => setFormData({ ...formData, landing_page_url: e.target.value })}
                                    placeholder="https://example.com/product"
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <Shield size={16} className="inline mr-2" />
                                    Whitelisted IPs (for postback security)
                                </label>
                                <input
                                    type="text"
                                    value={formData.whitelisted_ips}
                                    onChange={(e) => setFormData({ ...formData, whitelisted_ips: e.target.value })}
                                    placeholder="192.168.1.1, 10.0.0.0/24"
                                    className="input-field"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Comma-separated list of IPs or CIDR ranges. Leave empty to allow all.
                                </p>
                            </div>

                            <h3 className="text-lg font-semibold text-white">Targeting Rules</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Allowed Countries</label>
                                    <input
                                        type="text"
                                        value={formData.targeting_countries}
                                        onChange={(e) => setFormData({ ...formData, targeting_countries: e.target.value })}
                                        placeholder="US, CA, GB (empty = all)"
                                        className="input-field"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Allowed Devices</label>
                                    <input
                                        type="text"
                                        value={formData.targeting_devices}
                                        onChange={(e) => setFormData({ ...formData, targeting_devices: e.target.value })}
                                        placeholder="mobile, desktop (empty = all)"
                                        className="input-field"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Allowed OS</label>
                                    <input
                                        type="text"
                                        value={formData.targeting_os}
                                        onChange={(e) => setFormData({ ...formData, targeting_os: e.target.value })}
                                        placeholder="iOS, Android (empty = all)"
                                        className="input-field"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Fallback Redirect URL</label>
                                    <input
                                        type="url"
                                        value={formData.targeting_fallback}
                                        onChange={(e) => setFormData({ ...formData, targeting_fallback: e.target.value })}
                                        placeholder="https://..."
                                        className="input-field"
                                    />
                                </div>
                            </div>

                            <hr className="border-white/10" />

                            <h3 className="text-lg font-semibold text-white mt-6 mb-4">Additional Entry Links</h3>
                            <div className="space-y-4">
                                {formData.landing_pages.map((lp, index) => (
                                    <div key={index} className="flex gap-2 items-start bg-white/5 p-3 rounded-lg border border-white/5">
                                        <div className="flex-1 space-y-2">
                                            <input
                                                type="text"
                                                value={lp.name}
                                                onChange={(e) => {
                                                    const newLp = [...formData.landing_pages];
                                                    newLp[index].name = e.target.value;
                                                    setFormData({ ...formData, landing_pages: newLp });
                                                }}
                                                placeholder="Link Name (e.g. Advertorial)"
                                                className="input-field text-sm py-1.5"
                                            />
                                            <input
                                                type="url"
                                                value={lp.url}
                                                onChange={(e) => {
                                                    const newLp = [...formData.landing_pages];
                                                    newLp[index].url = e.target.value;
                                                    setFormData({ ...formData, landing_pages: newLp });
                                                }}
                                                placeholder="https://example.com/promo"
                                                className="input-field text-sm py-1.5"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newLp = [...formData.landing_pages];
                                                newLp.splice(index, 1);
                                                setFormData({ ...formData, landing_pages: newLp });
                                            }}
                                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors mt-1"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFormData({
                                            ...formData,
                                            landing_pages: [
                                                ...formData.landing_pages,
                                                { id: Date.now().toString(), name: '', url: '' }
                                            ]
                                        });
                                    }}
                                    className="btn-secondary w-full py-2 flex justify-center items-center gap-2"
                                >
                                    <Plus size={16} /> Add Entry Link
                                </button>
                            </div>

                            <hr className="border-white/10" />

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="active"
                                    checked={formData.active}
                                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                    className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-primary-500 focus:ring-primary-500"
                                />
                                <label htmlFor="active" className="text-gray-300">
                                    Product is active
                                </label>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={actionLoading}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                                >
                                    {actionLoading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Save size={18} />
                                            {editingProduct ? 'Update' : 'Create'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Creatives Management Modal - Full Editor */}
            {showCreativesModal && creativesProduct && (
                <CreativesEditor
                    product={creativesProduct}
                    onClose={() => setShowCreativesModal(false)}
                    onSave={() => {
                        fetchProducts();
                        setShowCreativesModal(false);
                    }}
                />
            )}
        </div>
    );
}
